import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useRef } from 'react';
import { db } from '../../firebase';
import type { AgoraRoomLike } from '../../types';
import {
  channelSessionDocId,
  clearCurrentUserCallState,
  clearFirestoreUsersForCurrentPage,
  getProfileRedirectUrl,
  markChannelSessionJoined,
} from '../../utils/videoCallFirestore';

/**
 * Listens to `video_call_sessions/{channelId}`.
 * When the host sets `ended: true`, every client in the channel disconnects and redirects.
 */
export default function useVideoCallChannelSession(room: AgoraRoomLike | null) {
  const roomRef = useRef(room);
  roomRef.current = room;

  const channelName = room?.channelName ?? null;

  useEffect(() => {
    if (!channelName) return undefined;

    const docId = channelSessionDocId(channelName);
    const sessionRef = doc(db, 'video_call_sessions', docId);
    let unsub: (() => void) | undefined;

    markChannelSessionJoined(channelName)
      .then(() => {
        unsub = onSnapshot(
          sessionRef,
          snapshot => {
            if (!snapshot.exists()) return;
            const data = snapshot.data();
            if (data?.ended === true) {
              void (async () => {
                try {
                  await clearFirestoreUsersForCurrentPage();
                } catch (e) {
                  console.error('clearFirestoreUsers (session ended):', e);
                }
                try {
                  await clearCurrentUserCallState(roomRef.current?.localParticipant?.identity, {
                    agoraUid: roomRef.current?.localParticipant?.uid,
                    channelName: roomRef.current?.channelName,
                  });
                } catch (e) {
                  console.error('clearCurrentUserCallState (session ended):', e);
                }
                try {
                  roomRef.current?.disconnect();
                } catch (e) {
                  console.error(e);
                }
                window.location.replace(getProfileRedirectUrl());
              })();
            }
          },
          err => console.error('video_call_sessions listener:', err)
        );
      })
      .catch(err => console.error('markChannelSessionJoined:', err));

    return () => {
      unsub?.();
    };
  }, [channelName]);
}
